import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { meApi } from '../services/api';
import { TAGLINE } from '../constants/brand';

// ── Constants ────────────────────────────────────────────────────────────────

const SPORT_ICONS = {
  badminton: '🏸', tennis: '🎾', table_tennis: '🏓',
  squash: '🟡', pickleball: '🥒', padel: '🎾',
};

const STATUS_META = {
  draft:             { label: 'Draft',    bg: '#f3f4f6', color: '#6b7280' },
  registration:      { label: 'Open',     bg: '#ecfdf5', color: '#059669' },
  registration_open: { label: 'Open',     bg: '#ecfdf5', color: '#059669' },
  seeding:           { label: 'Seeding',  bg: '#fef9c3', color: '#a16207' },
  in_progress:       { label: 'Live',     bg: '#fef3c7', color: '#d97706' },
  completed:         { label: 'Done',     bg: '#CCFBF1', color: '#5b21b6' },
};

const STAFF_ROLE_META = {
  co_organizer: { label: 'Co-organizer', bg: '#CCFBF1', color: '#5b21b6' },
  moderator:    { label: 'Moderator',    bg: '#f3e8ff', color: '#0F766E' },
  umpire:       { label: 'Umpire',       bg: '#fef3c7', color: '#b45309' },
};

const PERM_LABELS = {
  can_edit_settings:  'Settings',
  can_manage_players: 'Players',
  can_generate_draw:  'Draw',
  can_schedule:       'Schedule',
  can_score:          'Score',
};

const ROLE_META = {
  admin:     { label: 'Admin',     bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  organizer: { label: 'Organizer', bg: '#CCFBF1', color: '#5b21b6', border: '#c4b5fd' },
  player:    { label: 'Player',    bg: '#ecfdf5', color: '#065f46', border: '#a7f3d0' },
};

// ── Shared helpers ────────────────────────────────────────────────────────────

const card = {
  background: 'white', border: '1px solid #f0f0f0', borderRadius: 12,
  padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  textDecoration: 'none', display: 'block',
  transition: 'box-shadow 0.15s, transform 0.15s',
};
const hoverCard = (e, enter) => {
  e.currentTarget.style.boxShadow = enter ? '0 4px 16px rgba(0,0,0,0.08)' : '0 1px 4px rgba(0,0,0,0.04)';
  e.currentTarget.style.transform = enter ? 'translateY(-2px)' : 'translateY(0)';
};

const SectionHeader = ({ title, linkTo, linkLabel }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.875rem' }}>
    <h2 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', margin: 0 }}>{title}</h2>
    {linkTo && <Link to={linkTo} style={{ fontSize: '0.8rem', color: '#0D9488', textDecoration: 'none', fontWeight: 500 }}>{linkLabel || 'View all →'}</Link>}
  </div>
);

const EmptyCard = ({ icon, heading, sub, cta, ctaTo }) => (
  <div style={{ background: '#f9fafb', border: '1px dashed #e5e7eb', borderRadius: 12, padding: '2rem', textAlign: 'center' }}>
    <div style={{ fontSize: '2rem', marginBottom: 8 }}>{icon}</div>
    <p style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>{heading}</p>
    {sub && <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: 14 }}>{sub}</p>}
    {cta && <Link to={ctaTo} style={{ display: 'inline-flex', padding: '7px 16px', background: '#0D9488', color: 'white', borderRadius: 8, fontSize: '0.85rem', fontWeight: 500, textDecoration: 'none' }}>{cta}</Link>}
  </div>
);

// ── Tournament card (for owned tournaments) ───────────────────────────────────

const TournamentCard = ({ t }) => {
  const sm = STATUS_META[t.status] || STATUS_META.draft;
  return (
    <Link
      to={`/tournaments/${t.id}`}
      style={card}
      onMouseEnter={e => hoverCard(e, true)}
      onMouseLeave={e => hoverCard(e, false)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <span style={{ fontSize: '1.4rem' }}>{SPORT_ICONS[t.sport_type] || '🏅'}</span>
        <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: sm.bg, color: sm.color }}>{sm.label}</span>
      </div>
      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#111827', marginBottom: 3 }}>{t.name}</div>
      <div style={{ fontSize: '0.78rem', color: '#9ca3af' }}>
        {t.start_date ? new Date(t.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Date TBD'}
      </div>
    </Link>
  );
};

// ── Staff assignment card ─────────────────────────────────────────────────────

const AssignmentCard = ({ a }) => {
  const rm = STAFF_ROLE_META[a.staff_role] || { label: a.staff_role, bg: '#f3f4f6', color: '#6b7280' };
  const sm = STATUS_META[a.status] || STATUS_META.draft;
  const perms = Object.entries(PERM_LABELS).filter(([key]) => a[key]);
  return (
    <Link
      to={`/tournaments/${a.tournament_id}`}
      style={card}
      onMouseEnter={e => hoverCard(e, true)}
      onMouseLeave={e => hoverCard(e, false)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <span style={{ fontSize: '1.4rem' }}>{SPORT_ICONS[a.sport_type] || '🏅'}</span>
        <div style={{ display: 'flex', gap: 4 }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: sm.bg, color: sm.color }}>{sm.label}</span>
          <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: rm.bg, color: rm.color }}>{rm.label}</span>
        </div>
      </div>
      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#111827', marginBottom: 2 }}>{a.tournament_name}</div>
      <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: perms.length ? 10 : 0 }}>
        Organised by {a.organizer_name}
      </div>
      {perms.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {perms.map(([, label]) => (
            <span key={label} style={{ fontSize: '0.68rem', padding: '2px 7px', background: '#F0FDFA', color: '#5b21b6', borderRadius: 999, fontWeight: 500 }}>
              {label}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
};

// ── Quick action button ───────────────────────────────────────────────────────

const ActionBtn = ({ to, primary, children }) => (
  <Link
    to={to}
    style={{
      display: 'inline-flex', alignItems: 'center', padding: '8px 18px',
      background: primary ? '#0D9488' : 'white', color: primary ? 'white' : '#374151',
      borderRadius: 8, fontSize: '0.875rem', fontWeight: 500, textDecoration: 'none',
      border: primary ? 'none' : '1px solid #e5e7eb',
      boxShadow: primary ? '0 1px 3px rgba(13,148,136,0.25)' : 'none',
    }}
  >
    {children}
  </Link>
);

// ── Admin stats row ───────────────────────────────────────────────────────────

const StatCards = ({ stats }) => {
  const items = [
    { label: 'Users',           value: stats.total_users,        icon: '👥' },
    { label: 'Tournaments',     value: stats.total_tournaments,  icon: '🏆' },
    { label: 'Matches Played',  value: stats.completed_matches,  icon: '🎾' },
    { label: 'Players',         value: stats.total_players,      icon: '📋' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
      {items.map(s => (
        <div key={s.label} style={{ background: 'white', border: '1px solid #f0f0f0', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div style={{ fontSize: '1.4rem', marginBottom: 6 }}>{s.icon}</div>
          <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#111827', lineHeight: 1 }}>{(s.value ?? 0).toLocaleString()}</div>
          <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 4 }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
};

// ── Role-aware dashboard (authenticated) ──────────────────────────────────────

const Dashboard = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    meApi.getDashboard()
      .then(r => setData(r.data))
      .catch(() => setData({ staff_assignments: [], owned_tournaments: [] }))
      .finally(() => setLoading(false));
  }, []);

  const isAdmin     = user.role === 'admin';
  const isOrganizer = user.role === 'organizer' || isAdmin;
  const roleMeta    = ROLE_META[user.role] || ROLE_META.player;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh', color: '#9ca3af', fontSize: '0.9rem' }}>
        Loading your dashboard…
      </div>
    );
  }

  const assignments      = data?.staff_assignments     || [];
  const ownedTournaments = data?.owned_tournaments     || [];
  const stats            = data?.stats;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '2rem 1.25rem 4rem' }}>

      {/* ── Welcome header ── */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
          <h1 style={{ fontSize: '1.45rem', fontWeight: 700, color: '#111827', margin: 0 }}>
            Welcome back, {user.name}
          </h1>
          <span style={{
            fontSize: '0.68rem', fontWeight: 700, padding: '3px 10px', borderRadius: 999,
            border: `1px solid ${roleMeta.border}`, background: roleMeta.bg, color: roleMeta.color,
            letterSpacing: '0.05em', textTransform: 'uppercase',
          }}>
            {roleMeta.label}
          </span>
        </div>
        <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
          {isAdmin
            ? 'Manage the platform — users, tournaments, and player data.'
            : isOrganizer
              ? 'Your tournaments and staff assignments at a glance.'
              : assignments.length
                ? 'Your staff assignments and available tournaments.'
                : 'Browse upcoming tournaments and register to compete.'}
        </p>
      </div>

      {/* ── Admin platform stats ── */}
      {isAdmin && stats && <StatCards stats={stats} />}

      {/* ── Quick actions ── */}
      <div style={{ marginBottom: '2rem' }}>
        <p style={{ fontSize: '0.72rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.625rem' }}>
          Quick Actions
        </p>
        <div style={{ display: 'flex', gap: '0.625rem', flexWrap: 'wrap' }}>
          {isAdmin     && <ActionBtn to="/admin" primary>Admin Panel</ActionBtn>}
          {isOrganizer && <ActionBtn to="/create-tournament" primary>+ Create Tournament</ActionBtn>}
          {isOrganizer && <ActionBtn to="/clubs">Clubs &amp; Courts</ActionBtn>}
          <ActionBtn to="/tournaments">Browse Tournaments</ActionBtn>
          <ActionBtn to="/profile">My Profile</ActionBtn>
        </div>
      </div>

      {/* ── Organizer: own tournaments ── */}
      {isOrganizer && (
        <section style={{ marginBottom: '2.25rem' }}>
          <SectionHeader title="My Tournaments" linkTo="/tournaments" linkLabel="View all →" />
          {ownedTournaments.length === 0 ? (
            <EmptyCard icon="🏆" heading="No tournaments yet" sub="Create your first tournament to get started." cta="Create Tournament" ctaTo="/create-tournament" />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' }}>
              {ownedTournaments.map(t => <TournamentCard key={t.id} t={t} />)}
            </div>
          )}
        </section>
      )}

      {/* ── Staff assignments (anyone who has been added as staff) ── */}
      {assignments.length > 0 && (
        <section>
          <SectionHeader
            title={isOrganizer ? 'Staff Roles on Other Tournaments' : 'My Staff Assignments'}
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' }}>
            {assignments.map(a => <AssignmentCard key={a.staff_id} a={a} />)}
          </div>
        </section>
      )}

      {/* ── Player with no assignments: browse prompt ── */}
      {!isOrganizer && assignments.length === 0 && (
        <EmptyCard
          icon="🎾"
          heading="Find tournaments to join"
          sub="Browse upcoming tournaments in your sport and register to compete."
          cta="Browse Tournaments"
          ctaTo="/tournaments"
        />
      )}
    </div>
  );
};

// ── Marketing page (unauthenticated) ─────────────────────────────────────────

const features = [
  { icon: '🏆', title: 'Tournament Management', desc: 'Create and manage knockout, league, and playoff formats for any racket sport.' },
  { icon: '📊', title: 'Smart Draws', desc: 'Automatic seeded bracket generation with BYE handling and 3rd-place playoffs.' },
  { icon: '📅', title: 'Court Scheduling', desc: 'Schedule matches across multiple courts with conflict-free time assignment.' },
  { icon: '👥', title: 'Player Management', desc: 'Player registration, seeding, age/event categories and bulk CSV import.' },
  { icon: '📈', title: 'Analytics', desc: 'Organizer dashboards with player breakdowns, completion rates and revenue.' },
  { icon: '✉️', title: 'Email Notifications', desc: 'Automated emails for registration, draw release, schedules and results.' },
];
const sports = ['Badminton', 'Tennis', 'Table Tennis', 'Squash', 'Pickleball', 'Padel'];

const Marketing = () => (
  <div>
    <div style={{ background: 'linear-gradient(160deg, #f0fdfa 0%, #CCFBF1 40%, #F0FDFA 100%)', borderBottom: '1px solid #e8e5f8', padding: '5rem 1.25rem 4rem', textAlign: 'center' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <span style={{ display: 'inline-block', background: '#F0FDFA', color: '#0D9488', fontSize: '0.72rem', fontWeight: 600, padding: '4px 12px', borderRadius: 999, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '1.25rem', border: '1px solid #99F6E4' }}>
          Professional Tournament Management
        </span>
        <p style={{ fontSize: '1rem', color: '#0F766E', fontStyle: 'italic', fontWeight: 600, marginBottom: '1.5rem', letterSpacing: '0.03em' }}>
          {TAGLINE}
        </p>
        <h1 style={{ fontSize: 'clamp(2rem, 5vw, 2.8rem)', fontWeight: 800, color: '#0f172a', lineHeight: 1.15, marginBottom: '0.75rem', letterSpacing: '-0.025em' }}>
          Run perfect tournaments,{' '}
          <span style={{ background: 'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            effortlessly
          </span>
        </h1>
        <p style={{ fontSize: '1.05rem', color: '#6b7280', lineHeight: 1.7, marginBottom: '2rem' }}>
          From draws to scheduling to live scoring — everything you need to manage racket sport tournaments in one place.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/tournaments" style={{ display: 'inline-flex', alignItems: 'center', padding: '11px 24px', background: '#0D9488', color: 'white', borderRadius: 10, fontSize: '0.95rem', fontWeight: 600, textDecoration: 'none', boxShadow: '0 2px 8px rgba(13,148,136,0.3)' }}>
            Browse Tournaments
          </Link>
          <Link to="/register" style={{ display: 'inline-flex', alignItems: 'center', padding: '11px 24px', background: 'white', color: '#374151', borderRadius: 10, fontSize: '0.95rem', fontWeight: 600, textDecoration: 'none', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            Get Started Free
          </Link>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '2.5rem' }}>
          {sports.map(s => <span key={s} style={{ padding: '4px 12px', background: 'white', border: '1px solid #e5e7eb', borderRadius: 999, fontSize: '0.78rem', color: '#6b7280', fontWeight: 500 }}>{s}</span>)}
        </div>
      </div>
    </div>

    <div className="container" style={{ paddingTop: '4rem', paddingBottom: '4rem' }}>
      <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', marginBottom: '0.5rem' }}>Everything you need</h2>
        <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>A complete toolkit for tournament organizers and players.</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: '1.25rem' }}>
        {features.map(f => (
          <div key={f.title} style={{ background: 'white', border: '1px solid #f0f0f0', borderRadius: 14, padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', transition: 'box-shadow 0.15s, transform 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <div style={{ width: 42, height: 42, background: '#F0FDFA', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', marginBottom: '1rem' }}>{f.icon}</div>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#111827', marginBottom: '0.4rem' }}>{f.title}</h3>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', lineHeight: 1.6, margin: 0 }}>{f.desc}</p>
          </div>
        ))}
      </div>
      <div style={{ marginTop: '4rem', textAlign: 'center', background: 'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)', borderRadius: 16, padding: '3rem 2rem', color: 'white' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.75rem' }}>Ready to run your next tournament?</h2>
        <p style={{ opacity: 0.85, marginBottom: '1.5rem', fontSize: '1rem' }}>Sign up free — no credit card required.</p>
        <Link to="/register" style={{ display: 'inline-flex', alignItems: 'center', padding: '11px 24px', background: 'white', color: '#0D9488', borderRadius: 10, fontSize: '0.95rem', fontWeight: 600, textDecoration: 'none' }}>
          Create your account
        </Link>
      </div>
    </div>
  </div>
);

// ── Root component ────────────────────────────────────────────────────────────

const Home = () => {
  const { isAuthenticated, user } = useAuth();
  return isAuthenticated ? <Dashboard user={user} /> : <Marketing />;
};

export default Home;
